import React, { useEffect, useMemo, useRef, useState } from "react";
import clxs from "./cluster-gallery-modal.module.css";
import CLOSE_ICON from "../../../../assets/icons/close.svg";
import sortByAspectRatio from "../../../PropertyDetail/Gallery/sort-by-aspect-ratio";
import ScrollableTabs from "../../ScrollableTabs";
import clx from "classnames";
import YoutubeVideo from "../../../PropertyDetail/LohonoLayout/YoutubeVideo";
import useClx from "../../../../hooks/use-clx";
import fwclxs from "../../../../styles/full-width.module.css";
import Img from "../../../Img";
import createResponsiveSrc from "../../../../../utils/create-responsive-src";

interface ClusterGalleryModalProps {
  clusterVillas?: ClusterPropertyImages[];
  onSubmit?: (value: any) => void;
  villaSelected?:ClusterPropertyImages;
}

type TabData = {
  label: string;
  elementRef: React.RefObject<HTMLDivElement>;
};

function ClusterGalleryModal(props: ClusterGalleryModalProps) {
  const { clusterVillas, onSubmit ,villaSelected} = props;

  const ccx = useClx(
    fwclxs.container,
    fwclxs.containerPadding,
    clxs.galleryContent,
  );


  const MOBILE_WIDTH = 1024,
  DESKTOP_WIDTH = 1200, // or 1440, if your layout allows
  ASPECT_RATIO = 4 / 3,
  MOBILE_HEIGHT = Math.round(MOBILE_WIDTH / ASPECT_RATIO),
  DESKTOP_HEIGHT = Math.round(DESKTOP_WIDTH / ASPECT_RATIO);

  const [tabsData, setTabsData] = useState<TabData[]>([]);

  const villasRef = useRef<HTMLDivElement>(null);

  const sortedImagesList = useMemo(() => {
    return (
      clusterVillas?.map((villa) => sortByAspectRatio(villa.images || [])) || []
    );
  }, [clusterVillas]);

  // Refs for each villa container
  const villaRefs = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});

  // Generate refs and tabs after villas load
  useEffect(() => {
    if (clusterVillas && clusterVillas.length) {
      const refs: Record<string, React.RefObject<HTMLDivElement>> = {};
      const tabs: TabData[] = [];

      clusterVillas.forEach((villa) => {
        const ref = React.createRef<HTMLDivElement>();
        refs[villa.villa_name] = ref;

        tabs.push({
          label: villa.villa_name,
          elementRef: ref,
        });
      });

      villaRefs.current = refs;
      setTabsData(tabs);
    }
  }, [clusterVillas]);

  useEffect(() => {
    if (
      villaSelected &&
      villaRefs.current[villaSelected.villa_name]?.current
    ) {
      const ref = villaRefs.current[villaSelected.villa_name];
      const container = villasRef.current;
  
      if (ref?.current && container) {
        const elementPosition = ref.current.offsetTop;
        const scrollOffset = elementPosition - 60;
  
        container.scrollTo({
          top: scrollOffset,
          behavior: "smooth",
        });
      }
    }
  }, [villaSelected, tabsData,villasRef]); // <- include tabsData as dependency
  

  return (
    <div 
      className={clxs.container}
      ref={villasRef}
    >
      <div className={clxs.title}>
        <img
          src={CLOSE_ICON}
          alt="close"
          className={clxs.close}
          onClick={() => {
            // Reset scroll position
            villasRef.current?.scrollTo({ top: 0, behavior: "auto" });
            onSubmit?.(null);
          }}
        />
      </div>
      <ScrollableTabs
        tabsData={tabsData}
        className={clxs.scrollableTabs}
        parentRef={villasRef}
        headerOffset={80}
      />
      <div className={ccx}>
        {clusterVillas?.map((villa, index) => {
          const sortedImages = sortedImagesList[index];

          const ref = villaRefs.current[villa.villa_name];

          return (
            <div
              key={index}
              id={villa.villa_name}
              ref={ref}
              className={clxs.contentContainer}
            > 
              {villa.videos.length ? (
                <YoutubeVideo
                  anchorId={VIDEO_ANCHOR_ID}
                  youtubeVideoId={villa.videos[0].video_id}
                  className={clxs.videoContainer}
                />
              ) : null}
              <div className={clxs.imageContainer}>
                {sortedImages.map((each, i) => {
                  const { url, alt, caption, aspect_ratio } = each,
                    icx = clx(clxs.image, aspect_ratio),
                    isx = clx(clxs.imageDiv, aspect_ratio);

                    const {mobile : improved_mobile_url,desktop: improved_desktop_url} = createResponsiveSrc(url,75,50,[DESKTOP_WIDTH, DESKTOP_HEIGHT],[MOBILE_WIDTH, MOBILE_HEIGHT]);


                  return (
                    <div 
                      key={i} 
                      className={isx}
                    >
                      <Img
                        src={url}
                        alt={alt}
                        mobileSrc={improved_mobile_url}
                        desktopSrc={improved_desktop_url}
                        className={icx}
                        loading="lazy"
                        title={caption}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <span>&nbsp;</span>
    </div>
  );
}

export default ClusterGalleryModal;

const VIDEO_ANCHOR_ID = "video";
